/**
 * Heat calculations: q = mcΔT, calorimetry (mixing two bodies), latent heat q = mL and a
 * water heating curve.
 *
 * Working units: joules, grams, J/(g·°C) and kelvin (a kelvin difference equals a Celsius
 * difference). Every temperature must be above absolute zero, even though only the
 * difference matters in q = mcΔT, so an impossible entry is caught rather than quietly
 * accepted.
 */

import { MASS, type UnitDef } from "@/lib/tool-kit/chemistry/units";
import { parseMeasuredNumber } from "@/lib/tool-kit/math/number-input";
import { CALORIE } from "./constants";
import { findSpecificHeat, SPECIFIC_HEATS } from "./heat-data";
import { fromKelvin, toKelvin } from "./units";

/** Exact: the International Table British thermal unit, in joules. */
const BTU_IT = 1055.055_852_62;
const POUND_GRAMS = 453.592_37;

export const ENERGY_UNITS: readonly UnitDef[] = [
  { id: "J", symbol: "J", factor: 1 },
  { id: "kJ", symbol: "kJ", factor: 1000 },
  { id: "cal", symbol: "cal", factor: CALORIE },
  { id: "kcal", symbol: "kcal", factor: CALORIE * 1000 },
  { id: "Btu", symbol: "Btu", factor: BTU_IT },
];

/** Factors to J/(g·°C). 1 Btu/(lb·°F) = 4.1868 J/(g·K) exactly. */
export const SPECIFIC_HEAT_UNITS: readonly UnitDef[] = [
  { id: "JgC", symbol: "J/(g·°C)", factor: 1 },
  { id: "JgK", symbol: "J/(g·K)", factor: 1 },
  { id: "JkgK", symbol: "J/(kg·K)", factor: 1e-3 },
  { id: "kJkgK", symbol: "kJ/(kg·K)", factor: 1 },
  { id: "calgC", symbol: "cal/(g·°C)", factor: CALORIE },
  { id: "kcalkgC", symbol: "kcal/(kg·°C)", factor: CALORIE },
  {
    id: "BtulbF",
    symbol: "Btu/(lb·°F)",
    factor: BTU_IT / POUND_GRAMS / (5 / 9),
  },
];

/** Factors to J/g. 1 Btu/lb = 2.326 J/g exactly. */
export const LATENT_HEAT_UNITS: readonly UnitDef[] = [
  { id: "Jg", symbol: "J/g", factor: 1 },
  { id: "kJkg", symbol: "kJ/kg", factor: 1 },
  { id: "calg", symbol: "cal/g", factor: CALORIE },
  { id: "Btulb", symbol: "Btu/lb", factor: BTU_IT / POUND_GRAMS },
];

export const HEAT_MASS_UNITS: readonly UnitDef[] = MASS.units.filter((u) =>
  ["mg", "g", "kg", "oz", "lb"].includes(u.id)
);

export function unitFactor(units: readonly UnitDef[], id: string): number {
  return (units.find((u) => u.id === id) ?? units[0]).factor;
}

export function unitSymbol(units: readonly UnitDef[], id: string): string {
  return (units.find((u) => u.id === id) ?? units[0]).symbol;
}

export function unitOptionsOf(units: readonly UnitDef[]) {
  return units.map((u) => ({ value: u.id, label: u.symbol }));
}

/* -------------------------------------------------------- shared reading */

export type HeatFailure =
  | { ok: false; reason: "missing" }
  | {
      ok: false;
      reason:
        | "unreadable"
        | "nonPositive"
        | "belowZero"
        | "zeroValue"
        | "phaseMismatch";
      key: string;
    }
  | {
      ok: false;
      reason:
        | "zeroChange"
        | "signMismatch"
        | "resultBelowZero"
        | "outsideRange";
    };

export type Kind = "any" | "positive" | "nonzero" | "temperature";

/** The value of a typed entry in working units, or why it cannot be used. */
export function readQuantity(
  key: string,
  text: string,
  kind: Kind,
  unit: string,
  factor: number
): { value: number } | HeatFailure {
  if (text.trim() === "") {
    return { ok: false, reason: "missing" };
  }
  const typed = parseMeasuredNumber(text);
  if (typed === null) {
    return { ok: false, reason: "unreadable", key };
  }
  if (kind === "temperature") {
    const kelvin = toKelvin(typed, unit);
    return kelvin > 0
      ? { value: kelvin }
      : { ok: false, reason: "belowZero", key };
  }
  if (kind === "positive" && !(typed > 0)) {
    return { ok: false, reason: "nonPositive", key };
  }
  if (kind === "nonzero" && typed === 0) {
    return { ok: false, reason: "zeroValue", key };
  }
  return { value: typed * factor };
}

function isFailure(read: { value: number } | HeatFailure): read is HeatFailure {
  return "ok" in read;
}

/** Reads a set of entries, reporting a blank only after all the typed ones are checked. */
export interface ReadSpec {
  key: string;
  text: string;
  kind: Kind;
  unit: string;
  factor: number;
}

export function readAll(
  specs: readonly ReadSpec[]
): { values: Record<string, number> } | HeatFailure {
  const values: Record<string, number> = {};
  let missing = false;
  for (const spec of specs) {
    const read = readQuantity(
      spec.key,
      spec.text,
      spec.kind,
      spec.unit,
      spec.factor
    );
    if (!isFailure(read)) {
      values[spec.key] = read.value;
    } else if (read.reason === "missing") {
      missing = true;
    } else {
      return read;
    }
  }
  return missing ? { ok: false, reason: "missing" } : { values };
}

/* ----------------------------------------------------------- q = mcΔT */

export type HeatTerm = "q" | "m" | "c" | "ti" | "tf";
export const HEAT_TERMS: readonly HeatTerm[] = ["q", "m", "c", "ti", "tf"];

export interface HeatInput {
  unknown: HeatTerm;
  text: Record<HeatTerm, string>;
  /** Unit ids: energy, mass, specific heat, temperature (shared by Tᵢ and T_f). */
  unit: { q: string; m: string; c: string; t: string };
}

export interface HeatSolution {
  ok: true;
  unknown: HeatTerm;
  /** J, g, J/(g·°C), K, K */
  values: Record<HeatTerm, number>;
  /** T_f − T_i in kelvin (= °C). */
  delta: number;
  answer: number;
  answerUnit: string;
}

export type HeatOutcome = HeatSolution | HeatFailure;

const HEAT_KINDS: Record<HeatTerm, Kind> = {
  q: "any",
  m: "positive",
  c: "positive",
  ti: "temperature",
  tf: "temperature",
};

export function solveHeat(input: HeatInput): HeatOutcome {
  const { unknown, unit } = input;
  const factors: Record<HeatTerm, number> = {
    q: unitFactor(ENERGY_UNITS, unit.q),
    m: unitFactor(HEAT_MASS_UNITS, unit.m),
    c: unitFactor(SPECIFIC_HEAT_UNITS, unit.c),
    ti: 1,
    tf: 1,
  };
  const specs = HEAT_TERMS.filter((term) => term !== unknown).map((term) => ({
    key: term,
    text: input.text[term],
    kind: HEAT_KINDS[term],
    unit: term === "ti" || term === "tf" ? unit.t : "",
    factor: factors[term],
  }));
  const read = readAll(specs);
  if ("ok" in read) {
    return read;
  }
  const v = read.values;
  const value = heatFormula(unknown, v);
  if (value.ok === false) {
    return value;
  }
  const full = { ...v, [unknown]: value.value } as Record<HeatTerm, number>;
  if ((unknown === "ti" || unknown === "tf") && !(full[unknown] > 0)) {
    return { ok: false, reason: "resultBelowZero" };
  }
  return {
    ok: true,
    unknown,
    values: full,
    delta: full.tf - full.ti,
    answer: convertHeatAnswer(unknown, full[unknown], unit),
    answerUnit: answerUnitId(unknown, unit),
  };
}

function heatFormula(
  unknown: HeatTerm,
  v: Record<string, number>
): { ok: true; value: number } | HeatFailure {
  if (unknown === "q") {
    return { ok: true, value: v.m * v.c * (v.tf - v.ti) };
  }
  if (unknown === "tf") {
    return { ok: true, value: v.ti + v.q / (v.m * v.c) };
  }
  if (unknown === "ti") {
    return { ok: true, value: v.tf - v.q / (v.m * v.c) };
  }
  const delta = v.tf - v.ti;
  if (delta === 0) {
    return { ok: false, reason: "zeroChange" };
  }
  const value = unknown === "m" ? v.q / (v.c * delta) : v.q / (v.m * delta);
  return value > 0
    ? { ok: true, value }
    : { ok: false, reason: "signMismatch" };
}

function answerUnitId(unknown: HeatTerm, unit: HeatInput["unit"]): string {
  if (unknown === "ti" || unknown === "tf") {
    return unit.t;
  }
  return unit[unknown];
}

function convertHeatAnswer(
  unknown: HeatTerm,
  value: number,
  unit: HeatInput["unit"]
): number {
  if (unknown === "q") {
    return value / unitFactor(ENERGY_UNITS, unit.q);
  }
  if (unknown === "m") {
    return value / unitFactor(HEAT_MASS_UNITS, unit.m);
  }
  if (unknown === "c") {
    return value / unitFactor(SPECIFIC_HEAT_UNITS, unit.c);
  }
  return fromKelvin(value, unit.t);
}

/** A temperature *difference* in kelvin written in the reader's degree. */
export function deltaIn(kelvin: number, unit: string): number {
  return unit === "F" ? (kelvin * 9) / 5 : kelvin;
}

/** The specific heat table value in the unit the reader's menu is set to. */
export function tableValueIn(id: string, unit: string): number | null {
  const entry = findSpecificHeat(id);
  return entry ? entry.value / unitFactor(SPECIFIC_HEAT_UNITS, unit) : null;
}

/** The table substance whose specific heat is within `tolerance` of the value (J/(g·°C)). */
export function nearestSubstance(
  value: number,
  tolerance = 0.05
): { id: string; value: number } | null {
  let best: { id: string; value: number } | null = null;
  for (const entry of SPECIFIC_HEATS) {
    const gap = Math.abs(entry.value - value) / entry.value;
    const bestGap = best
      ? Math.abs(best.value - value) / best.value
      : tolerance;
    if (gap <= tolerance && gap <= bestGap) {
      best = { id: entry.id, value: entry.value };
    }
  }
  return best;
}
